import { WidgetPlacement } from '@wix/wix-chatbot-common/bi';
import { ChatbotView, Context } from '@wix/wix-chatbot-common/browser';

import {
  BlockId,
  BlockType,
  ButtonClicked,
  PresentedState,
  SupportType,
} from './helpCenter';

interface BiEventData {
  evid: number;
}

interface Bi574EventData extends BiEventData {
  action_url: string;
  call_center_status: null;
  estimated_wait_time: null;
  main_option_id: null;
  solution_time: null;
  solution_title: null;
  sub_option_id: null;
  sub_option_name: null;
  ticketing_status: null;
}

interface Bi575EventData extends Bi574EventData {
  conversation_id: string;
  link_name: string;
  link_url: string;
}

interface Bi1088EventData extends BiEventData {
  node_name: null;
  user_intent: null;
  linkUrl: string;
  button_type: string;
  node_id?: string;
  conversation_id?: string;
}

interface Bi1051EventData extends BiEventData {
  evid: number;
  ts: number;
  conversation_id?: string;
  referral: string | null;
  parent_url: string | null;
  placement: WidgetPlacement;
  nodeId?: string;
}

interface Bi573EventData extends BiEventData {
  evid: number;
  action_url: string;
  conversation_id: string;
  solution_time: null;
  solution_title: null;
  sub_option_id: null;
  sub_option_name: null;
  ticketing_status: null;
  call_center_status: null;
  estimated_wait_time: null;
  main_option_id: null;
}

interface Bi1054EventData extends BiEventData {
  evid: number;
  node_name: null;
  previous_node: null;
  previous_node_id: null;
  previous_node_name: null;
  user_intent: null;
  placement: WidgetPlacement;
  conversation_id?: string;
  node_id?: string;
}

export interface ArticleBlock {
  support_type: SupportType;
  support_id: string | null;
  support_position: number;
}

export interface SolutionInfo {
  block_id: BlockId;
  block_name: string;
  block_position: number;
  block_type: BlockType;
  block_details: readonly ArticleBlock[] | null;
  url?: string;
  text?: string;
  section_text?: string;
}

export enum KB_WIDGET_STATE {
  FULL_PAGE_EXTENDED = 'full_page_extended',
  FULL_PAGE_OPENED = 'full_page_opened',
}

export type KbWidgetStateBI = {
  kb_widget_state?: KB_WIDGET_STATE;
};

export interface HelpCenterEventData extends BiEventData {
  uuid: string;
  lng: string;
  placement: WidgetPlacement;
  solution_info: SolutionInfo | SolutionInfo[] | null;
  last_conversation_id?: string;
  last_chatbot_node_id?: string;
  last_user_intent_id?: string;
  ticket_id?: string;
  current_presented_state?: PresentedState;
  previous_presented_state?: PresentedState;
  lang?: string;
  article_id?: string | null; // need for A/B tests
  orig_page_name?: string;
  orig_platform?: string;
  category_id?: string;
}

export interface Bi1119EventDataRaw extends HelpCenterEventData {
  is_chatbot_solution_click?: boolean;
  article_recommendation_version?: string;
}

export interface Bi1120EventDataRaw extends HelpCenterEventData {
  button_clicked: ButtonClicked;
  clicked_value?: string;
  article_recommendation_version?: string;
}

export interface Bi1121EventDataRaw extends HelpCenterEventData {
  activity_type_mouse_click: boolean;
  activity_type_mouse_move: boolean;
  activity_type_scroll: boolean;
  activity_type_scroll_to_bottom: boolean;
  activity_type_widget_is_open: boolean;
  activity_type_scroll_added_content: boolean;
  max_scroll_at_first_position: boolean;
  max_scroll?: number;
}

export interface Bi1119EventData
  extends Omit<Bi1119EventDataRaw, 'solution_info'> {
  solution_info: string;
}

export interface Bi1120EventData
  extends Omit<Bi1120EventDataRaw, 'solution_info'> {
  solution_info: string;
}

export interface Bi1121EventData extends Bi1121EventDataRaw {}

export interface BiSolutionView {
  action_url: string;
  conversation_id: string;
}

export interface Bi1051Data {
  conversation_id?: string;
  nodeId?: string;
}
export interface DefaultBiFields {
  src: 4;
  bot_id: '717b0e2d-a564-4ef7-b965-481b1983133b';
  provider: 'answers';
  referral_url: string;
  referral: string | null;
  lang: string;
  parent_url: string | null;
}

export interface BaseBiEvent
  extends Partial<DefaultBiFields>,
    Partial<Context> {
  // context
  // regular fields
  user_intent?: string;
  main_option_name?: string;
  conversation_id?: string;
  session_id?: string;
  nodeId?: string;
  msid?: string;
  chatbot_view?: ChatbotView | null;
}

export interface SchedulerTimeslotTimeSelectedBiEvent
  extends Partial<BaseBiEvent> {
  evid: 1153;
  /** picked slot (day:hour) */
  info: string;
  local_time_zone: string;
}

export interface SchedulerTimeslotDateSelectedBiEvent
  extends Partial<BaseBiEvent> {
  evid: 1152;
  /** map of the new offered slots (day:hour) */
  info: string;
  local_time_zone: string;
}

export interface SchedulerTimeslotMonthArrowClickedBiEvent
  extends Partial<BaseBiEvent> {
  evid: 1151;
  local_time_zone: string;
}

export interface BaseStatusBannerBiEvent {
  incident_name: string;
  incident_severity: string;
  incident_state: string;
}
export interface StatusBannerShownBiEvent extends BaseStatusBannerBiEvent {
  evid: 1127;
}

export interface StatusBannerClickedBiEvent extends BaseStatusBannerBiEvent {
  evid: 1128;
}

export interface StatusBannerDismissedBiEvent extends BaseStatusBannerBiEvent {
  evid: 1129;
}

export enum SolutionTypes {
  ARTICLE = 'article',
}
export interface ClickOnSolutionBiEvent extends Partial<BaseBiEvent> {
  evid: 1159;
  solution_id: string;
  solution_type: SolutionTypes;
  current_presented_state: PresentedState;
  lng: string;
  current_page_name: string;
  placement: WidgetPlacement;
  orig_platform?: string;
  orig_page_name?: string;
  intent_id?: string;
}

export interface EmptySearchResultsReceivedBiEvent
  extends Partial<BaseBiEvent> {
  evid: 1160;
  orig_platform?: string;
  orig_page_name?: string;
}

export interface HelpCenterBreadcrumbsScrolledBiEvent
  extends Partial<BaseBiEvent> {
  evid: 1161;
  percent_scrolled: number;
  scroll_direction: string;
}

export interface ChatbotNotificationReceivedBiEvent
  extends Partial<BaseBiEvent> {
  evid: 1162;
  counter_value: number;
  placement: WidgetPlacement;
}

export type SendBiEventData =
  | Bi574EventData
  | Bi573EventData
  | Bi1054EventData
  | Bi1088EventData
  | Bi575EventData
  | Bi1051EventData
  | Bi1119EventData
  | Bi1120EventData
  | Bi1121EventData
  | SchedulerTimeslotTimeSelectedBiEvent
  | SchedulerTimeslotDateSelectedBiEvent
  | SchedulerTimeslotMonthArrowClickedBiEvent
  | StatusBannerShownBiEvent
  | StatusBannerClickedBiEvent
  | StatusBannerDismissedBiEvent
  | ClickOnSolutionBiEvent
  | EmptySearchResultsReceivedBiEvent
  | HelpCenterBreadcrumbsScrolledBiEvent
  | ChatbotNotificationReceivedBiEvent;
